/* global twemoji */
import React, { useMemo } from 'react'
import './DraftEmoji.less'
import { useStore } from '../../store/store';
import { twemojiCategories } from '../../utils/twemojiList';


// Fonction pour vérifier si un caractère est un emoji Unicode
const isEmoji = (char) => {
  // Vérifier si le caractère est dans les plages Unicode des emojis
  const codePoint = char.codePointAt(0);

  // Plages Unicode des emojis
  const emojiRanges = [
    [0x1F600, 0x1F64F], // Emoticons
    [0x1F300, 0x1F5FF], // Symboles et pictogrammes divers
    [0x1F680, 0x1F6FF], // Transport et symboles
    [0x1F1E0, 0x1F1FF], // Drapeaux
    [0x2600, 0x26FF],   // Symboles divers
    [0x2700, 0x27BF],   // Symboles décoratifs
    [0xFE00, 0xFE0F],   // Sélecteurs de variation
    [0x1F900, 0x1F9FF], // Symboles et pictogrammes étendus
    [0x1F018, 0x1F270], // Symboles divers
    [0x238C, 0x2454],   // Symboles techniques
    [0x20D0, 0x20FF],   // Symboles combinants
    [0x1F000, 0x1F02F], // Mahjong
    [0x1F0A0, 0x1F0FF], // Cartes à jouer
    [0x1F100, 0x1F64F], // Symboles alphanumériques
    [0x1F650, 0x1F67F], // Symboles ornementaux
    [0x1F680, 0x1F6FF], // Transport et symboles
    [0x1F780, 0x1F7FF], // Symboles géométriques
    [0x1F800, 0x1F8FF], // Flèches supplémentaires
    [0x1F900, 0x1F9FF], // Symboles et pictogrammes étendus
    [0x1FA00, 0x1FA6F], // Symboles d'échecs
    [0x1FA70, 0x1FAFF], // Symboles et pictogrammes étendus
    [0x1FAB0, 0x1FABF], // Symboles et pictogrammes étendus
    [0x1FAC0, 0x1FAFF], // Symboles et pictogrammes étendus
    [0x1FAD0, 0x1FAFF], // Symboles et pictogrammes étendus
    [0x1FAE0, 0x1FAFF], // Symboles et pictogrammes étendus
    [0x1FAF0, 0x1FAFF], // Symboles et pictogrammes étendus
  ];

  return emojiRanges.some(([start, end]) => codePoint >= start && codePoint <= end);
};

// Fonction pour vérifier si un caractère est dans la catégorie "Puces et symboles spéciaux"
const isSpecialSymbol = (char) => {
  const specialSymbols = twemojiCategories['Puces et symboles spéciaux'] || [];
  return specialSymbols.includes(char);
};

const getFontSizeFromInlineStyles = (inlineStyles) => {
  let fontSize = null;

  inlineStyles.forEach((style) => {
    const match = style.match(/FONT_SIZE_(\d+)/);
    if (match) {
      fontSize = parseInt(match[1], 10);
    }
  });

  return fontSize;
}
const DraftEmoji = (props) => {
  const { blockKey, contentState, start, end, inheritFontSize } = props;
  const fontSize = useStore(({ configuration }) => configuration.fontSize)
  const fromPdf = useStore((state) => state.fromPdf);

  const block = contentState.getBlockForKey(blockKey);

  const blockFontSize = useMemo(() => {

    for (let i = start; i < end; i++) {
      const inlineStyle = block.getInlineStyleAt(i);
      // `undefined` : pas de font-size sur le span, l'emoji hérite de son parent.
      return getFontSizeFromInlineStyles(inlineStyle) ?? (inheritFontSize ? undefined : fontSize)
    }
  }, [block, end, fontSize, inheritFontSize, start])

  const emojiUrl = useMemo(() => {
    if (typeof twemoji === 'undefined') return ""

    // Vérifier si le texte contient des emojis valides (pas de symboles spéciaux)
    const hasValidEmoji = Array.from(props.decoratedText).some(char => isEmoji(char) && !isSpecialSymbol(char));

    if (!hasValidEmoji) {
      return ""; // Retourner vide si aucun emoji valide
    }

    const twemojiElem = twemoji.parse(props.decoratedText, {
      folder: 'svg',
      ext: '.svg',
      base: 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@17.0.3/assets/'
    })
    if (!twemojiElem) return ""
    // Create a temporary DOM element to extract the URL
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = twemojiElem;

    // Find the image element and extract the `src` attribute
    const twemojiImg = tempDiv.querySelector('img'); // Find the first img element
    const emojiUrl = twemojiImg ? twemojiImg.src : null;

    return `url(${emojiUrl})`
  }, [props.decoratedText])

  // Vérifier si le texte contient des symboles spéciaux
  const hasSpecialSymbols = useMemo(() => {
    return Array.from(props.decoratedText).some(char => isSpecialSymbol(char));
  }, [props.decoratedText]);

  return (
    <>
      {!fromPdf && (
        hasSpecialSymbols ? (
          // Pour les symboles spéciaux, utiliser les polices système
          <span
            className="special-symbol"
            style={{
              fontSize: blockFontSize,
              display: 'inline',
              color: 'inherit',
              lineHeight: 'inherit'
            }}
          >
            {props.children}
          </span>
        ) : (
          // Pour les emojis normaux, utiliser twemoji
          <span
            style={{
              backgroundImage: emojiUrl,
              backgroundSize: "1em 1em",
              fontSize: blockFontSize,
              backgroundPosition: "center center",
              backgroundRepeat: 'no-repeat',
              WebkitTextFillColor: emojiUrl ? 'transparent' : 'inherit'
            }}
          >
            {props.children}
          </span>
        )
      )}
      {fromPdf && typeof twemoji !== 'undefined' && (
        // En PDF, le Chromium headless n'a aucune police emoji/dingbat système
        // (voir renderDraftContent.jsx) : le fallback "police système" utilisé
        // à l'écran pour les symboles spéciaux (☀, ▶, ⚡...) y produit un
        // glyphe invisible. On tente donc toujours twemoji ici — `parse()`
        // laisse le texte intact quand le caractère n'a pas d'asset connu
        // (ex. ★/►/▲/◆), donc pas de régression pour ces cas.
        <span
          style={{
            fontSize: blockFontSize,
          }}
          dangerouslySetInnerHTML={{
            __html: twemoji.parse(props.decoratedText, {
              folder: 'svg',
              ext: '.svg',
              base: 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@17.0.3/assets/'
            })
          }}
        />
      )}
    </>
  );
};

export default DraftEmoji
